'use client'

import { getHotkeyManager, type HotkeySequence, useHotkey } from '@repo/shortcuts'
import { useEffect } from 'react'

const canRunGlobalShortcutFrom = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false

  const tagName = target.tagName.toLowerCase()
  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') return false
  if (target.isContentEditable) return false
  if (target.closest('[role="textbox"]') !== null) return false
  if (target.closest('[role="menu"]') !== null) return false
  if (target.closest('[role="dialog"]') !== null) return false
  if (target.closest('[role="alertdialog"]') !== null) return false
  if (target.closest('[data-slot="command-dialog"]') !== null) return false
  // A surface with its own keys, such as the docked duplication side panel, which is no dialog.
  if (target.closest('[data-shortcut-scope]') !== null) return false

  return true
}

export const GlobalShortcutGuard = () => {
  useEffect(() => {
    const hotkeyManager = getHotkeyManager()
    let lastScope: Element | null = null
    const resetGuardedSequence = (event: Event) => {
      const scope =
        event.target instanceof HTMLElement ? event.target.closest('[data-shortcut-scope]') : null
      // `resetSequenceProgress` also clears a surface's own sequences, so a surface marked as a
      // shortcut scope keeps its progress while the keys stay inside it.
      if (scope !== lastScope || (!scope && !canRunGlobalShortcutFrom(event.target))) {
        hotkeyManager.resetSequenceProgress()
      }
      lastScope = scope
    }

    document.addEventListener('focusin', resetGuardedSequence, true)
    document.addEventListener('keydown', resetGuardedSequence, true)
    document.addEventListener('keyup', resetGuardedSequence, true)

    return () => {
      document.removeEventListener('focusin', resetGuardedSequence, true)
      document.removeEventListener('keydown', resetGuardedSequence, true)
      document.removeEventListener('keyup', resetGuardedSequence, true)
    }
  }, [])

  return null
}

export const useGlobalShortcut = (sequence: HotkeySequence, action: () => void, enabled = true) => {
  useHotkey(
    sequence,
    event => {
      if (!canRunGlobalShortcutFrom(event.target)) return

      event.preventDefault()
      event.stopPropagation()
      action()
    },
    // The library's input check also skips letters on focused Select triggers; the guards here
    // decide instead, and `GlobalShortcutGuard` clears progress typed into a field.
    { enabled, ignoreInputs: false, preventDefault: false, stopPropagation: false },
  )
}
